import { TimeIndicator } from "./TimeIndicator";
import { Camera } from "../Camera";
import { TimelineThemeGL } from "../TimelineThemeGL";
import { drawTimeAxisCanvas } from "./TimeAxisRenderer";
import {
  INonFlyingAssignment,
  IDisplayPairing,
  IDisplayBidPairing,
  INonFlyingPairingAssignment,
  IDisplayPositioningFlight,
  IPairingFlight,
  IPairingsLogicModifierGL,
  IPairingGroundEvent,
  IPairingLabelEvent,
  IDisplayBidNFA,
} from "@vmosolutions/oc-interfaces";
import { IAirportOptionMap } from "src/redux/reducers/StructuresReducer";
import { invertHexColor, fullColorHex } from "src/pages/FlightTimeline/TimelineUtils";
import { BaseTimelineGL } from "../BaseTimelineGL";
import {
  isINonFlyingAssignment,
  isINonFlyingPairingAssignment,
  IBaseTime,
  IDayLogicModifierMap,
  ICrewScheduleBaseEvent,
} from "src/redux/reducers/CrewScheduleReducer";
import { ScrollBarWrapper } from "../ScrollBar/ScrollBarWrapper";
import moment, { Moment } from "moment";
import { drawLogicModifiers, drawPairingLogicModifiers } from "src/pages/CrewSchedule/gl/LogicModifierGL";
import { CrewScheduleErrorsGL } from "src/pages/CrewSchedule/gl/CrewScheduleErrorsGL";
import { FlightGL } from "src/pages/FlightTimeline/gl/Flight/FlightGL";
import { milli2DaysHrsMin } from "src/utils";
import { IBiddingNFACounts } from "src/redux/actions/Bidding.Actions";
import { crewBidRowHeight } from "src/components/CrewSchedule/CrewMemberBidding";
import { FlightConnections } from "./FlightConnection";

export class BaseTimelineLabelsGL {
  protected textCanvas: HTMLCanvasElement;
  protected textCanvasCtx: CanvasRenderingContext2D;
  protected currentTimeIndicator: TimeIndicator;
  protected flightConnections: FlightConnections;
  protected textWidth = 0;
  protected textWidthLarger = 0;

  protected readonly FONT_DEFAULT: string;
  protected readonly FONT_WEIGHT = 300;
  protected readonly BASE_FONT_SIZE: number;
  protected readonly FLIGHT_NUMBER_FONT_SIZE: number;

  protected FontString(size = this.BASE_FONT_SIZE, weight = this.FONT_WEIGHT, font = this.FONT_DEFAULT): string {
    return `${weight} ${size}px '${font}'`;
  }

  protected SetFont(size?: number, weight?: number): void {
    this.textCanvasCtx.font = this.FontString(size, weight);
  }

  private biddingNFARowHeight: number;
  protected isICAO = true;
  private airportOptions: IAirportOptionMap = {};
  private hideNfaPriority = false;

  constructor(mount: HTMLElement, width: number, height: number, font = "Arial", baseFontSize = 10) {
    this.FONT_DEFAULT = font;
    this.BASE_FONT_SIZE = Math.max(8, baseFontSize * FlightGL.rowHeightPercentage + 2);
    this.FLIGHT_NUMBER_FONT_SIZE = Math.max(12, baseFontSize * FlightGL.rowHeightPercentage + 2);
    this.textCanvas = document.createElement("canvas");
    this.textCanvas.style.position = "absolute";
    this.textCanvas.style.top = "0";
    this.textCanvas.style.left = "0";
    this.setDimensions(width, height);
    this.textCanvasCtx = this.textCanvas.getContext("2d");

    this.SetFont();
    this.textWidth = this.textCanvasCtx.measureText("M").width * window.devicePixelRatio;

    mount.appendChild(this.textCanvas);

    this.currentTimeIndicator = new TimeIndicator();
    this.flightConnections = new FlightConnections();
    this.biddingNFARowHeight = 0;
  }

  private airportTextOffset = 28;
  private airportTimeOffset = 48;
  private padding = 4;
  private scrollBarStart = 0;

  public setBiddingNFARowHeight(count: number): void {
    this.biddingNFARowHeight = count;
  }

  public getBiddingNFARowHeight(): number {
    return this.biddingNFARowHeight;
  }

  public setHideNfaPriority(hide: boolean): void {
    this.hideNfaPriority = hide;
  }

  public setDimensions = (width: number, height: number): void => {
    const devicePixelRatio = window.devicePixelRatio || 1;

    this.textCanvas.width = width * devicePixelRatio;
    this.textCanvas.height = height * devicePixelRatio;

    this.textCanvas.style.width = `${width}px`;
    this.textCanvas.style.height = `${height}px`;
  };

  public clear = (): void => {
    this.textCanvasCtx.clearRect(0, 0, this.textCanvas.width, this.textCanvas.height);
  };

  public getContext(): CanvasRenderingContext2D {
    return this.textCanvasCtx;
  }

  public setAirportInfo = (isICAO: boolean, airportOptions: IAirportOptionMap): void => {
    this.isICAO = isICAO;
    this.airportOptions = airportOptions;
  };

  public prepareToDrawFlightLabels = (hexColor: string): void => {
    const fontSize = this.BASE_FONT_SIZE * window.devicePixelRatio;
    this.SetFont(fontSize);
    this.textCanvasCtx.fillStyle = "white";
    this.textCanvasCtx.textAlign = "start";
    this.textCanvasCtx.textBaseline = "middle";
    this.textWidth = this.textCanvasCtx.measureText("0").width * window.devicePixelRatio;
  };

  public drawTimeAxis = (
    camera: Camera,
    theme: TimelineThemeGL,
    baseTime: IBaseTime,
    biddingNFACounts?: IBiddingNFACounts
  ): void => {
    if (!theme) {
      return;
    }

    this.textCanvasCtx.save();
    const color = fullColorHex(
      Math.floor(theme.clearColor.red * 255),
      Math.floor(theme.clearColor.green * 255),
      Math.floor(theme.clearColor.blue * 255)
    );

    this.textCanvasCtx.fillStyle = "#" + color; //
    this.textCanvasCtx.fillRect(
      0,
      0,
      this.textCanvas.width,
      (BaseTimelineGL.timelineStart + this.biddingNFARowHeight) * window.devicePixelRatio
    );
    this.textCanvasCtx.restore();
    drawTimeAxisCanvas(this.textCanvasCtx, camera, theme, baseTime, biddingNFACounts);
  };

  public drawTimeIndicator = (theme: TimelineThemeGL, timeInPixels: number): void => {
    this.currentTimeIndicator.draw(this.textCanvasCtx, timeInPixels, theme.intervalBar);
  };

  public drawHoveredTimeIndicator = (theme: TimelineThemeGL, timeInPixels: number): void => {
    this.currentTimeIndicator.draw(this.textCanvasCtx, timeInPixels, theme.minorTimeColor);
  };

  public drawFlightConnections = (
    x1: number,
    y1: number,
    x2: number,
    y2: number,
    color: string,
    index: number
  ): void => {
    if (index === -1) return;
    this.flightConnections.draw(this.textCanvasCtx, x1, y1, x2, y2, color, index);
  };

  public drawNFALabels = (
    camera: Camera,
    nonFlyingAssingments: (INonFlyingAssignment | IDisplayBidNFA)[],
    rowStart: number,
    baseTime: IBaseTime
  ): void => {
    //per each row!

    /////// need to fix the Y coors //////
    // just calculate one per row
    const [yMin] = camera.getYRange();
    const y0: number = rowStart + (BaseTimelineGL.timelineStart + this.biddingNFARowHeight);
    const delta: number = y0 - yMin;
    const airportYPos = (delta + this.airportTextOffset) * window.devicePixelRatio;
    const timeYPos = (delta + this.airportTimeOffset) * window.devicePixelRatio;
    this.scrollBarStart = this.textCanvas.width - ScrollBarWrapper.scrollBarWidth;
    if (
      (BaseTimelineGL.timelineStart + this.biddingNFARowHeight) * window.devicePixelRatio >
      airportYPos - this.BASE_FONT_SIZE * window.devicePixelRatio
    ) {
      //if the labels are cutoff by the start fof the timeline don't draw anything in the row...
      return;
    }

    const labelPadding = 16;
    const [minX, maxX] = camera.getXRange();

    for (const nonFlying of nonFlyingAssingments) {
      //check to see if you should draw it
      if (nonFlying.start.valueOf() < maxX && nonFlying.end.valueOf() > minX) {
        const bidNFA = nonFlying as IDisplayBidNFA;
        const drawAsBidNFA = bidNFA?.hasOwnProperty("prefRank");
        // If staged, draw only in the staged row (unstaged row will get an arrow pointing up or down).
        const bidIsStaged =
          drawAsBidNFA && ((bidNFA?.assigned && !bidNFA?.awarded) || (!bidNFA?.assigned && bidNFA?.awarded));

        // updated to include buffer for new staging row
        const prefRankHeight = bidIsStaged
          ? 42 * window.devicePixelRatio
          : drawAsBidNFA
            ? (42 + bidNFA.prefRank * crewBidRowHeight + bidNFA.prefRank * 4) * window.devicePixelRatio
            : 0;
        this.drawNonFlyingAssignmentLabels(
          nonFlying,
          camera,
          labelPadding,
          airportYPos + prefRankHeight,
          timeYPos + prefRankHeight,
          baseTime
        );

        // If bid been assigned, render a double arrow pointing up or down based on whether it's being awarded or unawarded.
        if (bidNFA?.assigned) {
          this.drawBidAssignmentIndicators(
            bidNFA,
            camera,
            airportYPos + (38 + bidNFA.prefRank * crewBidRowHeight + bidNFA.prefRank * 4) * window.devicePixelRatio,
            bidNFA?.assigned && !bidNFA?.awarded
          );
        }
      }
    }
  };

  private drawBidAssignmentIndicators = (
    bid: IDisplayBidPairing | IDisplayBidNFA,
    camera: Camera,
    yPos: number,
    isAwardAssignment: boolean
  ): void => {
    const minFlightPixels = camera.timeAxisToPixelCoords(bid.start?.valueOf(), this.textCanvas.width);
    const startX = minFlightPixels >= 0 ? minFlightPixels : 0;
    const endX = camera.timeAxisToPixelCoords(bid.end?.valueOf(), this.textCanvas.width);
    const totalWidth = endX - startX;
    const doubleArrowSpacing = 5;
    const lineWidth = 2;

    // Conditional paramters (determined by wether bid is assigned for awarding or unawarding);
    const yStart = !isAwardAssignment ? yPos - 7 : yPos;
    const color = !isAwardAssignment ? "red" : "#88EC8C";
    const triangleWidth = !isAwardAssignment ? 6 : -6;
    const xStart = minFlightPixels + totalWidth / 2.0 - Math.abs(triangleWidth);

    // PAINT ALL ASSIGNMENT ARROWS
    this.textCanvasCtx.save();
    this.textCanvasCtx.lineWidth = lineWidth;
    this.textCanvasCtx.strokeStyle = color; // GREEN | RED
    this.textCanvasCtx.beginPath();
    this.textCanvasCtx.moveTo(xStart, yStart);
    this.textCanvasCtx.lineTo(xStart + Math.abs(triangleWidth), yStart + triangleWidth);
    this.textCanvasCtx.stroke();
    this.textCanvasCtx.lineTo(xStart + Math.abs(triangleWidth) * 2, yStart);
    this.textCanvasCtx.stroke();
    this.textCanvasCtx.moveTo(xStart, yStart + doubleArrowSpacing);
    this.textCanvasCtx.lineTo(xStart + Math.abs(triangleWidth), yStart + triangleWidth + doubleArrowSpacing);
    this.textCanvasCtx.stroke();
    this.textCanvasCtx.lineTo(xStart + Math.abs(triangleWidth) * 2, yStart + doubleArrowSpacing);
    this.textCanvasCtx.stroke();
    this.textCanvasCtx.beginPath();
    this.textCanvasCtx.restore();
  };

  private drawNonFlyingAssignmentLabels = (
    nonFlyingAssingment: INonFlyingAssignment | INonFlyingPairingAssignment | IDisplayBidNFA,
    camera: Camera,
    labelPadding: number,
    airportYPos: number,
    timeYPos: number,
    baseTime: IBaseTime,
    isNonFlyingPairingAssignment?: boolean
  ): void => {
    //need these to generate the time string
    const actualStart = new Date(nonFlyingAssingment.start);
    const actualEnd = new Date(nonFlyingAssingment.end);

    const minFlightPixels = camera.timeAxisToPixelCoords(nonFlyingAssingment.start?.valueOf(), this.textCanvas.width);
    const startX = minFlightPixels >= 0 ? minFlightPixels : 0;
    const endX = camera.timeAxisToPixelCoords(nonFlyingAssingment.end?.valueOf(), this.textCanvas.width);
    const totalWidth = endX - startX;

    this.textCanvasCtx.fillStyle = nonFlyingAssingment.assignmentType
      ? invertHexColor(nonFlyingAssingment.assignmentType.defaultColor)
      : "#FF0000";

    let location = "";
    if (isINonFlyingPairingAssignment(nonFlyingAssingment) && nonFlyingAssingment.location) {
      location = nonFlyingAssingment.location;
    } else if (isINonFlyingAssignment(nonFlyingAssingment) && nonFlyingAssingment.locationICAO) {
      location = this.isICAO
        ? nonFlyingAssingment.locationICAO
        : (this?.airportOptions?.[nonFlyingAssingment?.locationICAO]?.iata ?? nonFlyingAssingment.locationICAO);
    }
    const assignmentType = nonFlyingAssingment.assignmentType;
    const bidNFA = nonFlyingAssingment as IDisplayBidNFA;
    const showTier = !this.hideNfaPriority && typeof bidNFA?.tier === "number";
    const label = showTier
      ? `${assignmentType.title} ${bidNFA.tier}[${location}]`
      : `${assignmentType.title}[${location}]`;
    const flightNumberTextWidth = this.textWidth * label.length;
    if (totalWidth - labelPadding > flightNumberTextWidth) {
      const midX = minFlightPixels + totalWidth / 2.0;
      const fontSize = this.FLIGHT_NUMBER_FONT_SIZE * window.devicePixelRatio;
      this.textCanvasCtx.textAlign = "center";
      this.SetFont(fontSize, 300);
      this.textCanvasCtx.fillText(label, midX, airportYPos);
    }
  };

  public drawRestTimes = (
    camera: Camera,
    events: ICrewScheduleBaseEvent[],
    rowStart: number,
    baseTime: IBaseTime
  ): void => {
    const [yMin] = camera.getYRange();
    const y0: number = rowStart + (BaseTimelineGL.timelineStart + this.biddingNFARowHeight);
    const delta: number = y0 - yMin;
    const airportYPos = (delta + this.airportTextOffset) * window.devicePixelRatio;
    const timeYPos = (delta + this.airportTimeOffset) * window.devicePixelRatio;

    const [minX, maxX] = camera.getXRange();
    const labelPadding = 0;
    let lastDrawnIndex: number | undefined;
    for (let i = 0; i < events.length; i++) {
      //check to see if you should draw it - only if the time is in viewing range
      if (!!events[i + 1]?.start && events[i].end < maxX && events[i + 1].isDuty) {
        const shouldDrawRestTimeLabel = typeof lastDrawnIndex === "number" && !!events[lastDrawnIndex]?.end;
        if (shouldDrawRestTimeLabel) {
          this.drawRestTimeLabel(
            { start: events[i].end, end: events[i + 1].start, lastStart: events[lastDrawnIndex].end },
            camera,
            labelPadding,
            airportYPos,
            timeYPos,
            baseTime
          );
        }
        lastDrawnIndex = i + 1;
      }
    }
  };

  public drawPairingLabels = (
    camera: Camera,
    pairings: (IDisplayPairing | IDisplayBidPairing)[],
    rowStart: number,
    baseTime: IBaseTime,
    drawCheckInOutTimes = false
  ): void => {
    /////// need to fix the Y coors //////
    const [yMin] = camera.getYRange();
    const y0: number = rowStart + (BaseTimelineGL.timelineStart + this.biddingNFARowHeight);
    const delta: number = y0 - yMin;
    const airportYPos = (delta + this.airportTextOffset) * window.devicePixelRatio;
    const timeYPos = (delta + this.airportTimeOffset) * window.devicePixelRatio;
    const yStartOfLocationText = (delta + (this.airportTextOffset - this.BASE_FONT_SIZE)) * window.devicePixelRatio;

    if ((BaseTimelineGL.timelineStart + this.biddingNFARowHeight) * window.devicePixelRatio > yStartOfLocationText) {
      //dont draw
      return;
    }
    const [minX, maxX] = camera.getXRange();
    const labelPadding = 0;

    for (const pairing of pairings) {
      //check to see if you should draw it
      if (pairing.start.valueOf() < maxX && pairing.end.valueOf() > minX) {
        const bidPairing = pairing as IDisplayBidPairing;
        const drawAsBidPairing = bidPairing.hasOwnProperty("prefRank");
        // If staged, draw only in the staged row (unstaged row will get an arrow pointing up or down).
        const bidIsStaged =
          drawAsBidPairing &&
          ((bidPairing?.assigned && !bidPairing?.awarded) || (!bidPairing?.assigned && bidPairing?.awarded));
        // updated to include buffer for new staging row
        const prefRankHeight = bidIsStaged
          ? 42 * window.devicePixelRatio
          : drawAsBidPairing
            ? (42 + bidPairing.prefRank * crewBidRowHeight + bidPairing.prefRank * 4) * window.devicePixelRatio
            : 0;
        this.drawPairingLabel(
          pairing,
          camera,
          labelPadding,
          airportYPos + prefRankHeight,
          timeYPos + prefRankHeight,
          baseTime,
          drawCheckInOutTimes
        );

        // If bid been assigned, render a double arrow pointing up or down based on whether it's being awarded or unawarded.
        if (bidPairing?.assigned) {
          this.drawBidAssignmentIndicators(
            bidPairing,
            camera,
            airportYPos +
              (38 + bidPairing.prefRank * crewBidRowHeight + bidPairing.prefRank * 4) * window.devicePixelRatio,
            bidPairing?.assigned && !bidPairing?.awarded
          );
        }
      }
    }
  };

  public drawTimelineLogicModifiers = (camera: Camera, logicModifers: IDayLogicModifierMap, rowStart: number): void => {
    const [yMin] = camera.getYRange();
    const y0: number = rowStart + (BaseTimelineGL.timelineStart + this.biddingNFARowHeight);
    const delta: number = y0 - yMin;

    const yStart = (delta + CrewScheduleErrorsGL.yHeight) * window.devicePixelRatio;
    if (yStart <= (BaseTimelineGL.timelineStart + this.biddingNFARowHeight) * window.devicePixelRatio) {
      return;
    }
    drawLogicModifiers(this.textCanvasCtx, camera, logicModifers, yStart);
  };

  public drawTimelinePairingLogicModifiers = (
    camera: Camera,
    logicModifers: IPairingsLogicModifierGL[],
    y: number
  ): void => {
    const [yMin] = camera.getYRange();
    const y0: number = y + (BaseTimelineGL.timelineStart + this.biddingNFARowHeight);
    const delta: number = y0 - yMin;
    const yStart = (delta + CrewScheduleErrorsGL.yHeight) * window.devicePixelRatio;
    if (yStart < (BaseTimelineGL.timelineStart + this.biddingNFARowHeight) * window.devicePixelRatio) {
      return;
    }
    drawPairingLogicModifiers(this.textCanvasCtx, camera, logicModifers, yStart);
  };

  /** Function used to draw abitrary rectangles of a given size in the crew sched context (and optional time indicator bars) */
  public drawColoredRectangle = (
    rectangleSettings: { color: string; start: number; end: number; height: number },
    yPos: number,
    camera?: Camera,
    indicatorColor?: string,
    includeNFARowHeight?: boolean
  ): void => {
    if (!rectangleSettings) {
      return;
    }
    const { color, start, end, height } = rectangleSettings;
    // If camera is present, retrieve delta to render with vertical scroll otherwise assume static.
    const [yMin] = camera ? camera.getYRange() : [0];

    const devicePixelRatio = window.devicePixelRatio || 1;
    const timelineStartY = includeNFARowHeight
      ? BaseTimelineGL.timelineStart + this.biddingNFARowHeight
      : BaseTimelineGL.timelineStart; // don't include biddingNFARowHeight here as we
    const y0: number = yPos + timelineStartY;
    const delta: number = y0 - yMin;
    const yStart = delta * devicePixelRatio;
    const barHeight = devicePixelRatio * height;
    // Draw colored rectangle.
    this.textCanvasCtx.save();
    this.textCanvasCtx.fillStyle = color;
    this.textCanvasCtx.fillRect(start, yStart, end - start, barHeight);
    this.textCanvasCtx.restore();
    // Draw indicator bars if color is assigned.
    if (indicatorColor) {
      this.currentTimeIndicator.draw(this.textCanvasCtx, start, indicatorColor);
      this.currentTimeIndicator.draw(this.textCanvasCtx, end, indicatorColor);
    }
  };

  /**
   * If this isn't drawing the box very well around long text it might be that you aren't using a monospaced font
   * this.textCanvasCtx.measureText(Text).width might be best to pass in for w
   */
  public drawWarnBoxAroundText(x: number, y: number, w: number, h: number): void {
    this.textCanvasCtx.save();
    this.textCanvasCtx.beginPath();
    this.textCanvasCtx.lineWidth = 2;
    this.textCanvasCtx.setLineDash([2, 2]);
    this.textCanvasCtx.strokeStyle = "rgba(255,255,0,0.8)";
    this.textCanvasCtx.rect(x, y, w, h);
    this.textCanvasCtx.stroke();
    this.textCanvasCtx.restore();
  }

  private getStartMidMaxEvent = (event: any, camera: Camera): [number, number, number] => {
    //check to see which event type you are...
    const actualOut = new Date(event.start); //Block Time switch to Out/In
    const actualIn = new Date(event.end); //Block Time switch to Out/In

    const start = actualOut, //Block Time switch to Out/In
      end = actualIn; //Block Time switch to Out/In
    const minFlightPixels = camera.timeAxisToPixelCoords(start.getTime(), this.textCanvas.width);
    const startX = minFlightPixels >= 0 ? minFlightPixels : 0;
    const endX = camera.timeAxisToPixelCoords(end.getTime(), this.textCanvas.width);
    const totalWidth = endX - startX;
    const midX = minFlightPixels + totalWidth / 2.0;
    return [startX, midX, endX];
  };

  private drawLocation = (
    camera: Camera,
    event: IPairingLabelEvent,
    prevEvent: IPairingLabelEvent | null,
    nextEvent: IPairingLabelEvent | null,
    midYPos: number,
    ghostPairing = false
  ): void => {
    if (!nextEvent) {
      return;
    }
    const [, , endX] = this.getStartMidMaxEvent(event, camera);
    const originDestMatch = nextEvent.origin === event?.destination;
    const defaultFillColor = ghostPairing ? "#000000" : "#FFFFFF";
    this.textCanvasCtx.fillStyle = defaultFillColor; //just have the labels show as white
    const width = originDestMatch
      ? this.textWidth * event?.destination?.length
      : this.textWidth * (event?.destination?.length + nextEvent.origin?.length);
    const paddedWidth = 2 * (width + this.padding * 2);
    const originalTextAlign = this.textCanvasCtx.textAlign;
    const originText = this.isICAO
      ? nextEvent.origin
      : (this.airportOptions[nextEvent.origin]?.iata ?? nextEvent.origin);
    const destinationText = this.isICAO
      ? event?.destination
      : (this.airportOptions[event.destination]?.iata ?? nextEvent.origin);
    const curEndX = camera.timeAxisToPixelCoords(event?.end, this.textCanvas.width);
    const nextStartX = camera.timeAxisToPixelCoords(nextEvent.start, this.textCanvas.width); //Block Time switch to Out/In
    const availableWidth = nextStartX - endX;
    const midX = endX + availableWidth / 2;

    if (availableWidth > paddedWidth && midX + width / 2 < this.textCanvas.width - ScrollBarWrapper.scrollBarWidth) {
      if (!originDestMatch) {
        //is there space to draw both?
        if (availableWidth > paddedWidth && curEndX + width < this.textCanvas.width - ScrollBarWrapper.scrollBarWidth) {
          //draw destination of event
          this.drawWarnBoxAroundText(
            curEndX,
            midYPos - this.BASE_FONT_SIZE - 1,
            (this.textWidth * event?.origin?.length + this.padding) * window.devicePixelRatio,
            this.BASE_FONT_SIZE + this.padding
          );
          this.textCanvasCtx.textAlign = "start";
          this.textCanvasCtx.fillText(destinationText, curEndX + this.padding, midYPos);
          //draw origin of next event

          this.drawWarnBoxAroundText(
            nextStartX - width / 2 - this.padding / 2,
            midYPos - this.BASE_FONT_SIZE - 1,
            this.textWidth * nextEvent.origin?.length * window.devicePixelRatio,
            this.BASE_FONT_SIZE + this.padding
          );

          this.textCanvasCtx.textAlign = "start";
          this.textCanvasCtx.fillText(originText, nextStartX - width / 2 - this.padding / 2, midYPos);
        }
      } else {
        this.textCanvasCtx.textAlign = "center";
        this.textCanvasCtx.fillText(destinationText, midX, midYPos);
      }
    } else if (
      availableWidth > paddedWidth / 2 &&
      midX + width / 2 < this.textCanvas.width - ScrollBarWrapper.scrollBarWidth
    ) {
      this.textCanvasCtx.textAlign = "center";
      this.textCanvasCtx.fillStyle = originDestMatch ? defaultFillColor : "yellow";
      this.textCanvasCtx.fillText(originDestMatch ? destinationText : "⚠⚠⚠⚠", midX, midYPos);
      this.textCanvasCtx.fillStyle = defaultFillColor;
    } else if (
      availableWidth > paddedWidth / 6 &&
      midX + width / 4 < this.textCanvas.width - ScrollBarWrapper.scrollBarWidth
    ) {
      this.textCanvasCtx.textAlign = "center";
      this.textCanvasCtx.fillStyle = originDestMatch ? defaultFillColor : "yellow";
      this.textCanvasCtx.fillText(originDestMatch ? destinationText?.substr(-2) : "⚠⚠", midX, midYPos);
      this.textCanvasCtx.fillStyle = defaultFillColor;
    } else if (!originDestMatch && midX + width / 4 < this.textCanvas.width - ScrollBarWrapper.scrollBarWidth) {
      //show something if there is a mismatch of dest/origin
      this.textCanvasCtx.textAlign = "center";
      this.textCanvasCtx.fillStyle = originDestMatch ? defaultFillColor : "yellow";
      this.textCanvasCtx.fillText("⚠⚠", midX, midYPos);
      this.textCanvasCtx.fillStyle = defaultFillColor;
    }
    // }
    //don't draw the last events location
    // else {
    //   if (curEndX + width < this.textCanvas.width - ScrollBarWrapper.scrollBarWidth) {
    //     this.textCanvasCtx.textAlign = "start";
    //     this.textCanvasCtx.fillText(destinationText, endX + padding, midYPos * window.devicePixelRatio);
    //   }
    // }

    this.textCanvasCtx.textAlign = originalTextAlign;
  };

  private drawPairingLabel = (
    pairing: IDisplayPairing | IDisplayBidPairing,
    camera: Camera,
    labelPadding: number,
    airportYPos: number,
    timeYPos: number,
    baseTime: IBaseTime,
    drawCheckInOutTimes = false
  ): void => {
    //set the scrollbar start for
    this.scrollBarStart = this.textCanvas.width - ScrollBarWrapper.scrollBarWidth;
    const yStartOfLocationText = airportYPos - this.BASE_FONT_SIZE * window.devicePixelRatio;

    if (BaseTimelineGL.timelineStart + this.biddingNFARowHeight > yStartOfLocationText) {
      //dont draw
      return;
    }
    //does this pairing have removed events?
    let ghostPairing = false;

    //grab the label info for the rest periods
    const restLabelArray: IPairingLabelEvent[] = [];

    for (const eventGroup of pairing.eventGroups) {
      for (const nonFlying of eventGroup.nonFlyingAssignments) {
        this.drawNonFlyingAssignmentLabels(nonFlying, camera, labelPadding, airportYPos, timeYPos, baseTime, true);
        const location: string =
          isINonFlyingPairingAssignment(nonFlying) && nonFlying.location
            ? nonFlying.location
            : isINonFlyingAssignment(nonFlying) && nonFlying.locationICAO
              ? nonFlying.locationICAO
              : "";
        restLabelArray.push({
          eventType: "nfa",
          origin: location,
          destination: location,
          start: nonFlying.start,
          end: nonFlying.end,
        });
      }

      for (const positioningFlight of eventGroup.positioningFlights) {
        this.drawPositioningFlightLabels(positioningFlight, camera, labelPadding, airportYPos, timeYPos, baseTime);
        restLabelArray.push({
          eventType: "positioningFlight",
          origin: positioningFlight.origin,
          destination: positioningFlight.destination,
          start: positioningFlight.outTime,
          end: positioningFlight.inTime,
        });
        if (positioningFlight.removalType) {
          ghostPairing = true;
        }
      }

      for (const flight of eventGroup.flights) {
        this.drawPairingFlightLabels(flight, camera, labelPadding, airportYPos, timeYPos, baseTime);
        restLabelArray.push({
          eventType: "flight",
          origin: flight.origin,
          destination: flight.destination,
          start: flight.actualOut,
          end: flight.actualIn,
        });
        if (flight.removalType) {
          ghostPairing = true;
        }
      }

      for (const groundEvent of eventGroup.groundEvents) {
        this.drawPairingGroundEventLabels(groundEvent, camera, labelPadding, airportYPos, timeYPos, baseTime);
        restLabelArray.push({
          eventType: "groundEvent",
          origin: groundEvent.location,
          destination: groundEvent.location,
          start: new Date(groundEvent.actualStart)?.valueOf(),
          end: new Date(groundEvent.actualEnd)?.valueOf(),
        });
        //When the removed ground events are in, uncomment this
        // if(groundEvent.removalType) {
        //   ghostPairing = true;
        // }
      }
    }
    //draw locations between events
    const sortedArray = restLabelArray.sort((a: any, b: any) => (a.start < b.start ? -1 : a.start > b.start ? 1 : 0));

    sortedArray.forEach((event, index) => {
      const prevEvent = index > 0 ? sortedArray[index - 1] : null;
      const nextEvent = index + 1 < sortedArray.length ? sortedArray[index + 1] : null;
      this.drawLocation(camera, event, prevEvent, nextEvent, airportYPos, ghostPairing);
    });
    if (drawCheckInOutTimes) {
      //draw the checkin/checkout time...
      const minFlightPixels = camera.timeAxisToPixelCoords(pairing.start?.valueOf(), this.textCanvas.width);
      const startX = minFlightPixels >= 0 ? minFlightPixels : 0;
      const endX = camera.timeAxisToPixelCoords(pairing.end?.valueOf(), this.textCanvas.width);
      const totalWidth = endX - startX;
      const sText = this.getTimeString(new Date(pairing.start), baseTime);
      const eText = this.getTimeString(new Date(pairing.end), baseTime);

      const totalTextWidth = this.textCanvasCtx.measureText(sText).width * 2;

      if (totalWidth > totalTextWidth) {
        const prevTextAlign = this.textCanvasCtx.textAlign;
        this.textCanvasCtx.textAlign = "start";
        this.textCanvasCtx.fillText(sText, startX, timeYPos - 34);
        this.textCanvasCtx.textAlign = "end";
        this.textCanvasCtx.fillText(eText, endX, timeYPos - 34);
        this.textCanvasCtx.textAlign = prevTextAlign;
      }
    }
  };

  private drawPositioningFlightLabels = (
    positioningFlight: IDisplayPositioningFlight,
    camera: Camera,
    labelPadding: number,
    airportYPos: number,
    timeYPos: number,
    baseTime: IBaseTime
  ): void => {
    // -- draw positioning flight labels --
    if (!positioningFlight.outTime || !positioningFlight.inTime || !positioningFlight.hasOwnProperty("flightNumber")) {
      return;
    }

    let actualStart = new Date(positioningFlight.outTime);
    let actualEnd = new Date(positioningFlight.inTime);

    if (actualStart > actualEnd) {
      const temp = actualStart;
      actualStart = actualEnd;
      actualEnd = temp;
    }

    const minFlightPixels = camera.timeAxisToPixelCoords(positioningFlight.outTime, this.textCanvas.width);
    const startX = minFlightPixels >= 0 ? minFlightPixels : 0;
    const endX = camera.timeAxisToPixelCoords(positioningFlight.inTime, this.textCanvas.width);
    const totalWidth = endX - startX;
    //have the airlineIATA and the flight number
    const positioningFlightCodeNumber = positioningFlight?.airlineIATA
      ? positioningFlight.airlineIATA.concat(positioningFlight.flightNumber.toString())
      : positioningFlight.flightNumber.toString();
    const flightCodeNumberTextWidth = this.textWidth * positioningFlightCodeNumber.length;
    if (totalWidth - labelPadding > flightCodeNumberTextWidth) {
      const midX = minFlightPixels + totalWidth / 2.0;
      this.textCanvasCtx.textAlign = "center";
      let fontSize = this.FLIGHT_NUMBER_FONT_SIZE * window.devicePixelRatio;
      this.textCanvasCtx.font = this.FontString(fontSize, 700);
      const flightNumber: string =
        flightCodeNumberTextWidth + midX < this.textCanvas.width - ScrollBarWrapper.scrollBarWidth
          ? positioningFlightCodeNumber
          : "";
      this.textCanvasCtx.fillText(flightNumber, midX, airportYPos);
      fontSize = this.BASE_FONT_SIZE * window.devicePixelRatio;
      this.textCanvasCtx.font = this.FontString(fontSize, 300);
    }
  };

  private drawPairingFlightLabels = (
    flight: IPairingFlight,
    camera: Camera,
    labelPadding: number,
    airportYPos: number,
    timeYPos: number,
    baseTime: IBaseTime
  ): void => {
    let actualStart = new Date(flight.actualOut);
    let actualEnd = new Date(flight.actualIn);

    if (flight.actualOut > flight.actualIn) {
      const temp = actualStart;
      actualStart = actualEnd;
      actualEnd = temp;
    }

    const minFlightPixels = camera.timeAxisToPixelCoords(
      flight.actualOut > flight.actualIn ? flight.actualIn : flight.actualOut,
      this.textCanvas.width
    );
    const startX = minFlightPixels >= 0 ? minFlightPixels : 0;
    const endX = camera.timeAxisToPixelCoords(
      flight.actualOut > flight.actualIn ? flight.actualOut : flight.actualIn,
      this.textCanvas.width
    );
    const totalWidth = endX - startX;
    this.textCanvasCtx.fillStyle = invertHexColor(flight.flightType?.color ?? "#FFFFFF");

    //flight number

    const flightNumberTextWidth = this.textWidth * flight.flightNumber.toString().length;
    if (totalWidth - labelPadding > flightNumberTextWidth) {
      const midX = minFlightPixels + totalWidth / 2.0;
      this.textCanvasCtx.textAlign = "center";
      const flightNumber: string =
        midX + flightNumberTextWidth < this.scrollBarStart ? flight.flightNumber.toString() : "";
      let fontSize = this.FLIGHT_NUMBER_FONT_SIZE * window.devicePixelRatio;
      this.textCanvasCtx.font = this.FontString(fontSize, 300);
      this.textCanvasCtx.fillText(flightNumber, midX, airportYPos); //should I multiple airportYPos by window device
      fontSize = this.BASE_FONT_SIZE * window.devicePixelRatio;
      this.textCanvasCtx.font = this.FontString(fontSize, 300);
    }
  };

  private drawRestTimeLabel = (
    event: { start: number; end: number; lastStart: number },
    camera: Camera,
    labelPadding: number,
    airportYPos: number,
    timeYPos: number,
    baseTime: IBaseTime
  ): void => {
    const pixStart = camera.timeAxisToPixelCoords(event.start, this.textCanvas.width);
    const pixEnd = camera.timeAxisToPixelCoords(event.end, this.textCanvas.width);
    const label = milli2DaysHrsMin(event.end - event.lastStart);
    const restDur = pixEnd - pixStart;
    if (label && restDur > label.length * this.textWidth) {
      this.textCanvasCtx.textAlign = "center";
      this.textCanvasCtx.fillText(label, pixStart + restDur * 0.5, airportYPos);
      this.textCanvasCtx.strokeStyle = "rgba(255, 255, 255, 1)";
      this.textCanvasCtx.textAlign = "start";
    }
  };

  private drawPairingGroundEventLabels = (
    groundEvent: IPairingGroundEvent,
    camera: Camera,
    labelPadding: number,
    airportYPos: number,
    timeYPos: number,
    baseTime: IBaseTime
  ): void => {
    let actualStart = new Date(groundEvent.actualStart);
    let actualEnd = new Date(groundEvent.actualEnd);

    if (groundEvent.actualStart > groundEvent.actualEnd) {
      const temp = actualStart;
      actualStart = actualEnd;
      actualEnd = temp;
    }

    const minFlightPixels = camera.timeAxisToPixelCoords(
      groundEvent.actualStart > groundEvent.actualEnd
        ? new Date(groundEvent.actualEnd)?.valueOf()
        : new Date(groundEvent.actualStart)?.valueOf(),
      this.textCanvas.width
    );
    const startX = minFlightPixels >= 0 ? minFlightPixels : 0;
    const endX = camera.timeAxisToPixelCoords(
      groundEvent.actualStart > groundEvent.actualEnd
        ? new Date(groundEvent.actualStart)?.valueOf()
        : new Date(groundEvent.actualEnd)?.valueOf(),
      this.textCanvas.width
    );
    const totalWidth = endX - startX;
    //atm positing flights are always the same color
    this.textCanvasCtx.fillStyle = invertHexColor(groundEvent.eventType?.color ?? "#FF00FF");

    //flight number
    const flightNumberTextWidth = this.textWidth * groundEvent.eventType?.title?.toString().length;
    if (totalWidth - labelPadding > flightNumberTextWidth) {
      const midX = minFlightPixels + totalWidth / 2.0;
      this.textCanvasCtx.textAlign = "center";
      const flightNumber: string | undefined =
        midX + flightNumberTextWidth < this.scrollBarStart ? groundEvent.eventType?.title?.toString() : "";
      let fontSize = this.FLIGHT_NUMBER_FONT_SIZE * window.devicePixelRatio;
      this.textCanvasCtx.font = this.FontString(fontSize, 300);
      this.textCanvasCtx.fillText(flightNumber, midX, airportYPos);
      fontSize = this.BASE_FONT_SIZE * window.devicePixelRatio;
      this.textCanvasCtx.font = this.FontString(fontSize, 300);
    }
  };

  /**
   * drawAirportTime is a function to be used between pairings, flights
   * and day events
   */
  private drawAirportTimeGeneral = (
    airportText: string,
    timeText: string,
    min: number,
    max: number,
    isRightJustified = false,
    airportYPos: number,
    timeYPos: number,
    onPairing?: boolean,
    padding?: number
  ): number => {
    const width = max - min;
    const textWidth = airportText.length === 0 ? this.textWidth * timeText.length : this.textWidth * airportText.length;
    const startX = isRightJustified ? max - padding : min;
    if (isRightJustified) {
      this.textCanvasCtx.textAlign = "end";
    } else {
      this.textCanvasCtx.textAlign = "start";
    }
    const halfWidth = this.textWidth * 2;

    //as to not write on the scroll bar
    const endOfText: number = startX + textWidth;
    //adjust YPos for when showing on a pairing
    // const timeY: number = onPairing ? timeYPos : timeYPos - 10;

    if (width > textWidth && endOfText < this.scrollBarStart) {
      this.textCanvasCtx.fillText(airportText, startX, airportYPos);
      // this.textCanvasCtx.fillText(timeText, startX, timeY);
      return isRightJustified ? startX : startX + textWidth;
    } else if (width > halfWidth && endOfText < this.scrollBarStart) {
      const last2Chars = airportText.substring(airportText.length - 2);
      const shortenedStartX = isRightJustified ? startX : startX;
      this.textCanvasCtx.fillText(last2Chars, shortenedStartX, airportYPos);
      return isRightJustified ? shortenedStartX : shortenedStartX + halfWidth;
    }
    return 0;
  };

  /**
   * getTimeString - takes in a date and returns in the format 00:00
   * @param time - Date
   * @returns string
   */
  private getTimeString = (time: Date, baseTime: IBaseTime): string => {
    if (baseTime.isUTC) {
      const arrivalHoursStr = time.getUTCHours() < 10 ? `0${time.getUTCHours()}` : `${time.getUTCHours()}`;
      const arrivalMinsStr = time.getUTCMinutes() < 10 ? `0${time.getUTCMinutes()}` : `${time.getUTCMinutes()}`;
      return arrivalHoursStr + arrivalMinsStr;
    } else {
      const timeMap = getLocalTimeMapAt(baseTime.timezoneId, time.getTime());
      const hoursStr = timeMap.hours < 10 ? `0${timeMap.hours}` : `${timeMap.hours}`;
      const minutesStr = timeMap.minutes < 10 ? `0${timeMap.minutes}` : `${timeMap.minutes}`;
      return hoursStr + minutesStr;
    }
  };

  /**
   * Used just for testing stuff you can draw quick box and not change the context
   * @param x - x cords to draw the box (top left)
   * @param y - y cords to draw the box (top left)
   * @param width - the width, or default a 10x10 pixel box
   * @param height - the height, or default a 10x10 pixel box
   * @param color - the colour or defaults to red
   */
  protected drawTestBox = (x: number, y: number, width = 10, height = 10, color = "red"): void => {
    this.textCanvasCtx.save();
    this.textCanvasCtx.beginPath();
    this.textCanvasCtx.lineWidth = 1;
    this.textCanvasCtx.strokeStyle = color;
    this.textCanvasCtx.rect(x, y, width, height);
    this.textCanvasCtx.stroke();
    this.textCanvasCtx.restore();
  };
}

export interface IHoursMins {
  hours: number;
  minutes: number;
  utcOffset: number;
  utcMillis: number;
  localMoment: Moment;
  utcMoment: Moment;
}
export interface ILocalTimeMap {
  [key: string]: IHoursMins;
}

export const localTimeMap: ILocalTimeMap = {};

export const getLocalTimeMapAt = (timezoneCode: string, dateMillis: number): IHoursMins => {
  const timeMapKey = `${timezoneCode}-${dateMillis}`;
  if (!localTimeMap[timeMapKey]) {
    const localTime = moment(dateMillis).tz(timezoneCode);
    localTimeMap[timeMapKey] = {
      hours: localTime?.hours(),
      minutes: localTime?.minutes(),
      utcOffset: localTime?.utcOffset() * 60 * 1000,
      utcMillis: dateMillis,
      localMoment: localTime?.clone(),
      utcMoment: moment(dateMillis).utc(),
    };
  }

  return localTimeMap[timeMapKey];
};
